<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { BaseAlert, BaseButton, BaseDialog, BasePasswordInput } from "@lunagoai/vue-ui";
import { changeAccountPassword, logout } from "../../api";
import { clearSession } from "../../app/session";

const props = defineProps<{ visible: boolean }>();
const emit = defineEmits<{ "update:visible": [visible: boolean] }>();

const router = useRouter();
const currentPassword = ref("");
const password = ref("");
const changingPassword = ref(false);
const passwordError = ref("");
const visible = computed({
  get: () => props.visible,
  set: (value: boolean) => emit("update:visible", value),
});

watch(() => props.visible, (isVisible) => {
  if (!isVisible) return;
  currentPassword.value = "";
  password.value = "";
  passwordError.value = "";
});

function close(): void {
  if (changingPassword.value) return;
  visible.value = false;
}

async function submit(): Promise<void> {
  if (changingPassword.value) return;
  if (!currentPassword.value || password.value.length < 12) {
    passwordError.value = "新密碼至少需要 12 個字元。";
    return;
  }

  changingPassword.value = true;
  passwordError.value = "";
  try {
    await changeAccountPassword(currentPassword.value, password.value);
    await logout().catch(() => undefined);
    clearSession();
    visible.value = false;
    password.value = "";
    await router.replace("/login");
  } catch (cause) {
    passwordError.value = cause instanceof Error ? cause.message : "account_password_change_failed";
  } finally {
    changingPassword.value = false;
  }
}
</script>

<template>
  <BaseDialog v-model:visible="visible" header="變更密碼" max-width="md" :closable="!changingPassword" :close-on-escape="!changingPassword" :dismissable-mask="!changingPassword" :draggable="false" content-class="bg-slate-900! border-slate-700!" body-class="bg-slate-900!">
    <form class="space-y-5" @submit.prevent="submit">
      <p class="text-sm leading-6 text-slate-300">設定新密碼後，系統會結束目前登入階段，請使用新密碼重新登入。</p>
      <BasePasswordInput v-model="currentPassword" label="目前密碼" autocomplete="current-password" :show-strength="false" />
      <BasePasswordInput v-model="password" label="新密碼" autocomplete="new-password" :show-strength="false" />
      <p class="text-xs text-slate-500">至少 12 個字元</p>
      <BaseAlert v-if="passwordError" severity="error">{{ passwordError }}</BaseAlert>
    </form>
    <template #footer>
      <BaseButton variant="secondary" label="取消" :disabled="changingPassword" @click="close" />
      <BaseButton variant="primary" label="更新密碼" :loading="changingPassword" :disabled="!currentPassword || password.length < 12" @click="submit" />
    </template>
  </BaseDialog>
</template>
